import React, { useState } from 'react'
import {Elements, useElements, useStripe} from '@stripe/react-stripe-js';
import {loadStripe, Stripe} from '@stripe/stripe-js';
import { AgTechQuery, AgTechQueryProp, AgTechQueryResponses, useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import AgTechQueryLoadingSurface from '@agtech/core/data/queries/AgTechQueryLoadingSurface';
import { useAgTechWebAppContext } from '../Contexts/AgTechWebAppContext';
import { AgTechLoader } from '../../Pages/Loading/AgTechLoadingSurface';

type AgTechBaseProductInfo = {
    paymentSecret: string
}

export type AgTechStripeContextProps<TProductInfo extends AgTechBaseProductInfo> = {
    publishableKey: string,
    productInfoQuery: AgTechQueryProp<any, TProductInfo>
    confirmPaymentQuery: AgTechQueryProp<any, boolean>,
    callbacks: {
        onSuccessfulPayment: () => Promise<void>
    },
    content?: {
        confirmationHeader?: string,
        confirmationDetails?: string
    },
    children: (productData: TProductInfo) => JSX.Element
}

export type AgTechStripeData = {
    clientSecret: string,
    stripeData: Stripe
}

const AgTechStripeContext = <TProductInfo extends AgTechBaseProductInfo>(
    props: AgTechStripeContextProps<TProductInfo>
) => {
    const paymentSecretQuery = useAgTechQuery({
        query: props.productInfoQuery.query,
        queryContext: props.productInfoQuery.queryContext
    });

    return (
        <AgTechQueryLoadingSurface query={paymentSecretQuery}>
            {productInfo => (
                <AgTechStripeElements
                    {...props}
                    productInfo={productInfo}
                />
            )}
        </AgTechQueryLoadingSurface>
    )
}

const AgTechStripeElements = <TProductInfo extends AgTechBaseProductInfo>(
    props: AgTechStripeContextProps<TProductInfo> & { productInfo: TProductInfo }
) => {
    const stripeDataQuery = useAgTechQuery({
        query: AgTechStripeDataQuery,
        queryContext: {
            publishableKey: props.publishableKey,
            paymentSecret: props.productInfo.paymentSecret
        }
    });

    return (
        <AgTechQueryLoadingSurface query={stripeDataQuery}>
            {(data) => (
                <Elements
                    stripe={data.stripeData}
                    options={{ 
                        clientSecret: data.clientSecret,
                        appearance: AgTechStripeStyles,
                    }}
                >
                    <AgTechStripeCheckoutForm {...props} />
                </Elements>
            )}
        </AgTechQueryLoadingSurface>
    );
}

const AgTechStripeCheckoutForm = <TProductInfo extends AgTechBaseProductInfo>(
    props: AgTechStripeContextProps<TProductInfo> & { productInfo: TProductInfo }
) => {
    const stripe = useStripe();
    const elements = useElements();
    const appContext = useAgTechWebAppContext();

    const [purchaseState, setPurchaseState] = useState<'CheckingOut' | 'Processing' | 'Confirming' | 'Complete'>('CheckingOut');

    const isProcessing = purchaseState === 'Processing' || purchaseState === 'Confirming';
    const isComplete = purchaseState === 'Complete';

    useAgTechQuery({
        query: props.confirmPaymentQuery.query,
        queryContext: props.confirmPaymentQuery.queryContext,
        settings: {
            refetchInterval: (result) => {
                // Retry every 3 seconds if the payment hasn't been confirmed
                return result.state.data ? false : 3000;
            },
            // Only attempt to confirm payment if checkout has completed
            enabled: purchaseState === 'Confirming',
            eventHandlers: {
                onQuerySuccess: (data) => {
                    if (data) {
                        setPurchaseState('Complete');

                        setTimeout(() => {
                            props.callbacks.onSuccessfulPayment();
                        }, 500);
                    }
                }
            }
        }
    });
    
    const handleSubmit = async (event: any) => {
        // We don't want to let default form submission happen here,
        // which would refresh the page.
        event.preventDefault();
    
        if (!stripe || !elements) {
            // Stripe.js hasn't yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setPurchaseState('Processing');
    
        const result = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: window.location.href,
            },
            redirect: 'if_required'
        });

        if (result.paymentIntent) {
            setPurchaseState('Confirming');
        }
        else if (result.error) {
            appContext.toasts.showError({
                header: 'An error occurred processing the payment',
                details: result.error.message
            });
        }
    }

    return (
        <form className='stretched flex' onSubmit={handleSubmit}>
            {isProcessing && <AgTechLoader text='Processing purchase...' />}
            {isComplete && appContext.loading.actions.renderActionConfirmation({
                confirmationMessage: props.content?.confirmationHeader ?? 'Payment successful',
                confirmationDetails: props.content?.confirmationDetails ?? 'Thank you for your purchase!'
            })}
            <div className={isProcessing || isComplete ? 'hidden' : 'flex-col stretched flex'}>
                {props.children(props.productInfo)}
            </div>
        </form>
    )
}

const AgTechStripeDataQuery: AgTechQuery<{ publishableKey: string, paymentSecret: string }, AgTechStripeData> = {
    queryKey: () => ['Stripe'],
    query: async props => {
        const stripe = await loadStripe(props.inputData.publishableKey);

        return stripe ? AgTechQueryResponses.Success({
            clientSecret: props.inputData.paymentSecret,
            stripeData: stripe
        })
        : AgTechQueryResponses.Failure('Error loading stripe');
    },
    content: {
        loadingText: 'Loading payment...',
        failureText: 'Error loading payment'
    }
}

const AgTechStripeStyles = {
    variables: {
        colorPrimary: '#5469d4',
        colorBackground: 'white',
        colorText: 'black',
        spacingUnit: '4px',
    },
    rules: {
        '.Label': {
            fontSize: '13px',
            fontWeight: '500',
            marginBottom: '8px'
        },
        '.Input': {
            borderColor: '#ced4da',
            boxShadow: 'none',
            borderRadius: '2px'
        },
        '.Input:focus': {
            borderColor: '#5469d4',
        },
    },
};

export default React.memo(AgTechStripeContext) as typeof AgTechStripeContext;